// src/services/authService.js
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { COGNITO_CONFIG } from "../config/cognitoConfig";
import { jwtDecode } from "jwt-decode";

const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.UserPoolId,
  ClientId: COGNITO_CONFIG.ClientId,
});

/**
 * Maps Cognito error codes to user-friendly messages
 */
export const cognitoErrorMessages = {
  NotAuthorizedException: "Incorrect username or password",
  UserNotFoundException: "Account doesn't exist with this username",
  UserNotConfirmedException: "Please verify your account before signing in",
  PasswordResetRequiredException: "You need to reset your password",
  TooManyRequestsException: "Too many attempts. Please try again later",
  LimitExceededException: "Too many attempts. Please try again later",
  CodeMismatchException: "Invalid verification code",
  ExpiredCodeException:
    "Verification code has expired. Please request a new one",
  InvalidPasswordException:
    "Password must be at least 8 characters with uppercase, lowercase, numbers and special characters",
  InvalidParameterException:
    "Invalid parameters provided. Please check your input",
  // Default fallback
  default: "An error occurred. Please try again later",
};

/**
 * Get user-friendly error message from Cognito error
 * @param {Error} error - The error from Cognito API
 * @returns {string} User-friendly error message
 */
export const getCognitoErrorMessage = (error) => {
  console.error("Cognito error:", error);
  return (
    cognitoErrorMessages[error.code] ||
    error.message ||
    cognitoErrorMessages.default
  );
};

/**
 * Sign in user with Cognito
 * @param {string} email - Username/email
 * @param {string} password - User password
 * @returns {Promise} Promise resolving to user data or error
 */

export const signInQ = (email, password) => {
  const user = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const idToken = session.getIdToken().getJwtToken();
        // ✅ Decode the token
        const decodedToken = jwtDecode(idToken);

        const group = decodedToken["cognito:groups"]?.[0] || "";
        let userRole = "";

        switch (group) {
          case "Admin":
            userRole = "super_admin";
            break;
          case "National_Sales_Manager":
            userRole = "super_admin";
            break;
          case "Avana_Employee":
            userRole = "manager";
            break;
          case "Distributor_Admin":
            userRole = "distributor_admin";
            break;
          case "Distributor":
            userRole = "application_specialist";
            break;
          default:
            userRole = "";
        }

        const userData = {
          accessToken: session.getAccessToken().getJwtToken(),
          idToken,
          refreshToken: session.getRefreshToken().getToken(),
          userRole,
          // userRole:
          //   decodedToken["cognito:groups"][0] === "Admin" ? "super_admin" : "", // Array of groups
          customUserId: decodedToken["custom:UserID"] || null, // Your custom attribute
          firstName: decodedToken["name"] || null,
          lastName: decodedToken["nickname"] || "",
          email: decodedToken["email"] || "",
        };
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("user", JSON.stringify(userData));
        console.log("userData", userData);
        resolve(userData);
      },
      onFailure: (err) => {
        reject({
          code: err.code,
          message: getCognitoErrorMessage(err),
        });
      },
      newPasswordRequired: (userAttributes) => {
        // Handle the case where user needs to set a new password
        reject({
          code: "NewPasswordRequired",
          message:
            "You need to set a new password. Please contact administrator.",
          userAttributes,
        });
      },
    });
  });
};

/**
 * Change password for authenticated user
 * @param {string} oldPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise} Promise resolving to success or error
 */
export const changePassword = (oldPassword, newPassword) => {
  const currentUser = userPool.getCurrentUser();
  
  if (!currentUser) {
    return Promise.reject({
      success: false,
      code: "UserNotAuthenticated",
      message: "No user is currently authenticated",
    });
  }

  return new Promise((resolve, reject) => {
    // Get the current session to ensure user is authenticated
    currentUser.getSession((err, session) => {
      if (err) {
        reject({
          success: false,
          code: err.code,
          message: getCognitoErrorMessage(err),
        });
        return;
      }

      if (!session.isValid()) {
        reject({
          success: false,
          code: "SessionInvalid",
          message: "Your session has expired. Please sign in again.",
        });
        return;
      }

      // Change the password
      currentUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject({
            success: false,
            code: err.code,
            message: getCognitoErrorMessage(err),
          });
        } else {
          resolve({
            success: true,
            message: "Password changed successfully",
            data: result,
          });
        }
      });
    });
  });
};

/**
 * Initiate forgot password flow
 * @param {string} email - Username/email
 * @returns {Promise} Promise resolving to success or error
 */
export const forgotPassword = (email) => {
  const user = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: (data) => {
        resolve({
          success: true,
          message: "Verification code sent successfully to your email",
          data,
        });
      },
      onFailure: (err) => {
        reject({
          success: false,
          code: err.code,
          message: getCognitoErrorMessage(err),
        });
      },
      inputVerificationCode: (data) => {
        resolve({
          success: true,
          message:
            "Verification code sent successfully. Please check your email",
          data,
        });
      },
    });
  });
};

/**
 * Confirm forgot password with verification code
 * @param {string} email - Username/email
 * @param {string} code - Verification code sent to user's email
 * @param {string} newPassword - New password
 * @returns {Promise} Promise resolving to success or error
 */
export const confirmForgotPassword = (email, code, newPassword) => {
  console.log("data", email, code, newPassword);
  const user = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve({
          success: true,
          message:
            "Password reset successful. You can now sign in with your new password.",
        });
      },
      onFailure: (err) => {
        reject({
          success: false,
          code: err.code,
          message: getCognitoErrorMessage(err),
        });
      },
    });
  });
};

/**
 * Check if a user is currently authenticated
 * @returns {boolean} True if user is authenticated
 */
export const isAuthenticated = () => {
  const currentUser = userPool.getCurrentUser();
  if (!currentUser) return false;

  return localStorage.getItem("isAuthenticated") === "true";
};

/**
 * Sign out current user
 */
export const signOut = () => {
  const currentUser = userPool.getCurrentUser();
  if (currentUser) {
    currentUser.signOut();
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("user");
  }
};
